import { type PropsWithChildren, createContext, use, useEffect, useState } from "react";
import { useSearchParams } from "wouter";

import type { z } from "zod";
import { InstanceIdContext } from "./instance";
import { Instance } from "./model";
import { fullPageCentred } from "./styles";

export type AuthContextType = {
  username: string;
  role: string;
  firstName: string;
  lastName: string;
};

// biome-ignore lint: null
export const AuthContext = createContext<AuthContextType>(null!!!);

export const UserSchema = Instance.shape.users.element;
export type UserType = z.infer<typeof UserSchema>;

const Login = () => {
  const { instanceId } = use(InstanceIdContext);
  const [searchParams] = useSearchParams();

  const error = searchParams.has("error") && (
    <div role="alert" className="alert alert-error mt-5">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6 shrink-0 stroke-current"
        fill="none"
        viewBox="0 0 24 24"
      >
        <title>Alert</title>
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>That didn't work ☹️</span>
    </div>
  );

  return (
    <div style={fullPageCentred}>
      <form method="POST" action={`/api/login/${instanceId}`}>
        <fieldset className="fieldset w-xs bg-base-200 border border-base-300 p-4 rounded-box">
          <legend className="fieldset-legend">Login</legend>

          <label htmlFor="username" className="fieldset-label">
            Username
          </label>
          <input name="username" type="text" className="input" placeholder="...@tbxofficial.com" />

          <label htmlFor="password" className="fieldset-label">
            Password
          </label>
          <input name="password" type="password" className="input" placeholder="p3d4!s4e\/a" />

          <button className="btn btn-soft mt-3" type="submit">
            Login
          </button>
        </fieldset>
      </form>
      {error}
    </div>
  );
};

export const AuthProvider = (props: PropsWithChildren) => {
  const { instanceId } = use(InstanceIdContext);
  const [auth, setAuth] = useState<AuthContextType>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setTimeout(async () => {
      try {
        const resp = await fetch(`/api/who-am-i/${instanceId}`);
        if (resp.status !== 200) {
          return;
        }

        const user = UserSchema.parse(await resp.json());

        setAuth({
          username: user.username,
          role: user.role,
          firstName: user.first_name,
          lastName: user.last_name,
        });
      } catch (e) {
        // TODO: handle error
      } finally {
        setLoading(false);
      }
    });
  }, [instanceId]);

  if (loading) {
    return (
      <div style={fullPageCentred}>
        <span className="loading loading-infinity loading-xl" />
      </div>
    );
  }

  if (!auth) {
    return <Login />;
  }

  return <AuthContext value={auth}>{props.children}</AuthContext>;
};
